<script setup lang="ts">
/**
 * @author Beka Chkhaidze
 */
import { ref } from "vue";
import { reqAxios } from "@/composables/core/func";

import { useMainStore } from "@/store/main/index";
import { useBuildingStore } from "@/store/building";

import { useRoute, useRouter } from "vue-router";

import { useRecaptcha } from "@/composables/core/recaptcha";
// import { sendEvent } from "@/composables/utils/sendEvent";

import { required, maxLength, minLength } from "@vuelidate/validators";
import { useFormValidator, DEFAULT_RULES } from "@/composables/utils/formValidator";

import Input from "@/components/regularComponents/FormComponents/Input.vue";
import Button from "@/components/regularComponents/UIComponents/Button.vue";
import Recaptcha from "@/components/regularComponents/UIComponents/Recaptcha.vue";
import SelectInput from "~/components/regularComponents/FormComponents/SelectInput.vue";
import BaseModal from "@/components/regularComponents/UIComponents/Modals/BaseModal.vue";
import Checked from "@/components/regularComponents/UIComponents/SVG/popup/Checked.vue";
import X from "@/components/regularComponents/UIComponents/SVG/popup/X.vue";

import type { SelectInterface } from "@/types/FormInterface";

// const SelectInput = defineAsyncComponent(
//   () => import("@/components/regularComponents/FormComponents/SelectInput.vue")
// );

const PhoneInput = defineAsyncComponent(
  () => import("@/components/regularComponents/UIComponents/PhoneInput.vue")
);

const buildingStore = useBuildingStore();
const mainStore = useMainStore();
const route = useRoute();
const router = useRouter();

const rules = {
  ...DEFAULT_RULES,
  email: { ...DEFAULT_RULES.email, required },
  project: { required },
  message: { required, minLength: minLength(10), maxLength: maxLength(1000) },
};

const formData = ref<{
  project: (SelectInterface & { id?: number }) | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  message: string | null;
}>({
  project: null,
  name: null,
  email: null,
  phone: null,
  message: null,
});
const dial = ref(null);
const isRecaptchaSolved = ref(false);
const recaptchaKey = ref(0);
const isLoading = ref(false);
const isDone = ref(false);
const isModalOpen = ref(false);
const isSuccess = ref(false);

const {
  isFormValid,
  validationStarted,
  errors,
  isValidationFromBtnSubmit,
  doValidation,
  resetState,
} = useFormValidator({
  formData,
  rules,
});

const onClose = () => {
  isLoading.value = false;
  isSuccess.value = false;
  isModalOpen.value = false;
  const hasQuery = route.query.hasOwnProperty(mainStore.autoOpenModalQueryKey);
  if (hasQuery) {
    router.replace({
      query: {
        ...route.query,
        [mainStore.autoOpenModalQueryKey]: undefined,
      },
    });

    setTimeout(() => {
      mainStore.hasIgnorableQuery = false;
    }, 800);
  }
};

const onSubmit = async () => {
  doValidation(true);
  const token = await useRecaptcha();
  isRecaptchaSolved.value = Boolean(token);
  if (!isFormValid.value || !isRecaptchaSolved.value) return;

  const params = {
    ...formData.value,
    project: formData.value.project?.title,
    phone: `+${dial.value}${formData.value.phone}`,
    projectId: formData.value.project?.id,
  };

  isLoading.value = true;

  const res = await reqAxios("main/saveSubmitedForm", {
    formType: "contactForm",
    ...params,
  });

  if (res?.data["0"] !== "error" && res.status === 200) {
    isLoading.value = false;
    isDone.value = true;
    isSuccess.value = true;
    isModalOpen.value = true;

    // sendEvent("?thankyou");

    // sendEvent("Contact Page - Contact us form submission");

    mainStore.hasIgnorableQuery = true;
    router.push({ query: { ...route.query, [mainStore.autoOpenModalQueryKey]: null } });
  } else {
    isModalOpen.value = true;
    isSuccess.value = false;
  }

  setTimeout(() => {
    isDone.value = false;
  }, 4000);

  recaptchaKey.value++;
  resetState();
};
</script>

<template>
  <div class="contact-us__message">
    <div class="contact-us__message-title uppercase">
      <p>{{ $t("want to talk?") }}</p>
      <p>{{ $t("leave us a message.") }}</p>
    </div>
    <form class="contact-us__message-form">
      <ClientOnly>
        <SelectInput
          v-model="formData.project"
          class="mb-16 projects"
          :items="buildingStore.getActiveProjectsNames || []"
          :label="$t('project')"
          :errorText="errors?.project"
          required
        />
      </ClientOnly>
      <Input
        v-model="formData.name"
        class="mb-16"
        required
        :label="$t('name')"
        :errorText="errors?.name"
      />
      <div class="d-flex al-start j-btw mb-16 contact-us__message-form-phonemail">
        <Input
          v-model="formData.email"
          class="input-mail"
          required
          :label="$t('email')"
          :errorText="errors?.email"
        />
        <ClientOnly>
          <PhoneInput
            class="input-phone"
            v-model="formData.phone"
            v-model:dial="dial"
            required
            :errorText="errors?.phone"
          />
        </ClientOnly>
      </div>

      <div class="mb-16">
        <textarea
          maxlength="1000"
          v-model="(formData.message as string)"
          :placeholder="$t('message') + ' *'"
          :class="['textarea', { 'textarea--error': errors?.message }]"
        />
        <Transition name="fade">
          <div v-if="errors?.message" class="custom-input__additional is-error mb-16">
            {{ errors?.message }}
          </div>
        </Transition>
      </div>

      <div class="mb-16">
        <Recaptcha :key="recaptchaKey" />
      </div>

      <Button
        class="submit-btn"
        @click.prevent="onSubmit"
        :isLoading="isLoading"
        :isDone="isDone"
        btnText="submit message"
        :btnType="1"
      />
    </form>
  </div>
    <Teleport to="body">
      <Transition name="fade" mode="out-in">
        <BaseModal v-if="isModalOpen" @close="onClose">
          <div class="message-submit">
            <div class="message-submit__icon">
              <Checked v-if="isSuccess" class="j-center" />
              <X v-else class="j-center" />
            </div>

            <p class="message-submit__title">
              {{ $t(isSuccess ? "thank you!" : "sorry!") }}
            </p>
            <p class="message-submit__teaser">
              <template v-if="isSuccess">
                {{
                  $t("your meeting with gumbati consultant was successfully scheduled")
                }}
              </template>
              <template v-else>
                {{
                  $t(
                    "your meeting with gumbati consultant was not scheduled. please try again!"
                  )
                }}
              </template>
            </p>
            <Button @click="onClose" full btn-text="done" />
          </div>
        </BaseModal>
      </Transition>
    </Teleport>
</template>
